import type { ClientCompany } from '../types';

import * as React from 'react';
import { useLocation } from 'react-router-dom';

import { Api } from '../api';

type ClientData = Readonly<{
  user: Api.User.V_Self;
  clientCompanies: ReadonlyArray<ClientCompany>;
  agencyCompanies: ReadonlyArray<Api.Company.V_AdminList>;
  ticketLabels: Api.TicketLabel.BaseView[];
}>;

const ClientDataContext = React.createContext<ClientData | null>(null);
export default ClientDataContext;

export function useClientData(): ClientData {
  const data = React.useContext(ClientDataContext);
  if (!data)
    throw new Error(
      'useClientData must be used within ClientDataContext.Provider'
    );
  return data;
}

export function useCompany(
  slug: string
): Api.Company.V_AdminList | ClientCompany {
  const { agencyCompanies, clientCompanies } = useClientData();
  const allCompanies = [...clientCompanies, ...agencyCompanies];
  const result = allCompanies.find((c) => c.slug === slug);
  if (!result) throw new Error(`Company not found with slug ${slug}`);
  return result;
}

export function useSelectedCompany(): Api.Company.V_AdminList | ClientCompany {
  const location = useLocation();
  const matches = location.pathname.match(/^\/company\/([^/]+)/);
  const selectedCompanySlug = matches ? matches[1] : null;
  if (!selectedCompanySlug)
    throw new Error('Calling useSelectedCompany with no company in url');
  return useCompany(selectedCompanySlug);
}
